import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Input } from '@devforce/tds-react';
import {buildScopeTranslate} from 'lib/I18n';
import Form from 'components/form/Form';
import createReleaseImage from 'images/trailmaker/create-release.svg';

const maxApiNameLength = 32;

const tRelease = buildScopeTranslate('views.trailmaker.release');
const tActions = buildScopeTranslate('views.trailmaker.actions');

export default class CreateReleaseForm extends React.PureComponent {
  static propTypes = {
    releasesHref: PropTypes.string.isRequired,
    release: PropTypes.object.isRequired
  };

  state = {
    label: this.props.release.label || '',
    api_name: this.props.release.api_name || '',
    autoFillApiName: !this.props.release.api_name,
    hasAttemptedSubmit: false,
    errors: {
      label: [],
      api_name: []
    },
    disableCreateButton: false
  };

  onChangeLabel = (event) => {
    const {api_name, autoFillApiName, hasAttemptedSubmit, errors} = this.state;
    const label = (event.target.value).replace(/\s+/g, ' ');
    const newApiName = (autoFillApiName ? label.trim() : api_name).replace(/\W+/g, '-');
    
    // Re-validate on change if user has already attempted to submit
    if (hasAttemptedSubmit) {
      const newErrors = {
        ...errors,
        label: this.validateLabelField(label)
      };
      
      // If Release Name affects API name, also re-validate API name
      if (autoFillApiName) {
        newErrors.api_name = this.validateApiNameField(newApiName);
      }
      
      this.setState({
        label,
        api_name: newApiName,
        errors: newErrors
      });
    } else {
      this.setState({
        label,
        api_name: newApiName
      });
    }
  }

  onChangeApiName = (event) => {
    const apiName = event.target.value;

    // Re-validate on change if user has already attempted to submit
    if (this.state.hasAttemptedSubmit) {
      this.setState({
        api_name: apiName,
        autoFillApiName: false,
        errors: {
          ...this.state.errors,
          api_name: this.validateApiNameField(apiName)
        }
      });
    } else {
      this.setState({
        api_name: apiName,
        autoFillApiName: false
      });
    }
  }

  // Helper method to validate label field
  validateLabelField = (label) => {
    const errors = [];
    if (!label.trim()) {
      errors.push(tRelease('release_label_required'));
    }
    return errors;
  }

  // Helper method to validate API name field
  validateApiNameField = (apiName) => {
    const errors = [];
    if (!apiName.trim()) {
      errors.push(tRelease('api_name_required'));
    } else if (apiName.length > maxApiNameLength) {
      errors.push(tRelease('api_name_length_error'));
    }
    return errors;
  }

  getErrorsFor(...fields) {
    const {release: {errors}} = this.props;
    if (isEmpty(errors)) return [];

    return fields.reduce((acc, field) => {
      const messages = errors[field];
      return isEmpty(messages) ? acc : acc.concat(messages);
    }, []);
  }

  getApiNameErrors() {
    if (this.state.errors.api_name.length > 0) {
      return this.state.errors.api_name;
    }
    return this.getErrorsFor('api_name', 'base')[0];
  }

  validateForm = () => {
    const {label, api_name} = this.state;
    return {
      label: this.validateLabelField(label),
      api_name: this.validateApiNameField(api_name)
    };
  }

  handleSubmit = (event) => {
    const errors = this.validateForm();
    const hasErrors = errors.label.length > 0 || errors.api_name.length > 0;

    if (hasErrors) {
      event.preventDefault();
      event.stopPropagation();
      
      this.setState({
        hasAttemptedSubmit: true,
        errors
      }, () => {
        // Focus the first invalid field
        if (errors.label.length > 0) {
          document.getElementById('form-release-label')?.focus();
        } else if (errors.api_name.length > 0) {
          document.getElementById('form-release-api_name')?.focus();
        }
      });
      
      return false;
    }
    // If no errors, allow the form to submit normally
    return true;
  }

  renderHeader() {
    return (
      <header className="slds-text-align_center slds-border_bottom slds-p-around_medium">
        <h1 className="slds-text-heading_large th-text--bold th-color--secondary">
          {tRelease('create_title')}
        </h1>
      </header>
    );
  }

  renderFooter() {
    return (
      <footer className="slds-border_top slds-p-horizontal_large slds-p-vertical_medium slds-border_top slds-grid slds-grid_align-spread th-bg--light">
        <a
          href={this.props.releasesHref}
          className="slds-button slds-button--neutral">
          {tActions('cancel')}
        </a>

        <input
          type="submit"
          className="slds-button slds-button--brand"
          value={tActions('create')}
          onClick={this.handleSubmit}
          disabled={this.state.disableCreateButton}
        />
      </footer>
    );
  }

  renderFields() {
    const {hasAttemptedSubmit, errors} = this.state;
    
    // Always show server errors, only show client errors after submit attempt
    const labelError = this.getErrorsFor('label')[0] || (hasAttemptedSubmit ? errors.label[0] : undefined);
    const apiNameError = this.getErrorsFor('api_name', 'base')[0] || (hasAttemptedSubmit ? errors.api_name[0] : undefined);

    // Unique IDs for error messages
    const labelErrorId = 'form-release-label-error';
    const apiNameErrorId = 'form-release-api_name-error';

    return (
      <div>
        <Input
          required
          id="form-release-label"
          name="form_release[label]"
          label={tRelease('release_label')}
          errorText={labelError}
          errorId={labelErrorId}
          value={this.state.label}
          onChange={this.onChangeLabel}
          placeholder="Sample Release"
          maxLength="100"
          aria-describedby={labelError ? labelErrorId : undefined}
        />

        <Input
          required
          id="form-release-api_name"
          name="form_release[api_name]"
          label={tRelease('api_name')}
          errorText={apiNameError}
          errorId={apiNameErrorId}
          value={this.state.api_name}
          onChange={this.onChangeApiName}
          placeholder="sample-release"
          maxLength={String(maxApiNameLength)}
          aria-describedby={apiNameError ? apiNameErrorId : undefined}
        />
      </div>
    );
  }

  render() {
    const {releasesHref} = this.props;

    return (
      <Form
        method="post"
        action={releasesHref}
        disableButton={(disableCreateButton) => this.setState({ disableCreateButton })}
        noValidate
        className="slds-form slds-form_stacked">
        {this.renderHeader()}

        <div className="slds-grid slds-wrap slds-grid_vertical-align-center">
          <div className="slds-col slds-p-around_large">
            {this.renderFields()}
          </div>

          <div className="slds-show_medium slds-p-around_large">
            <img
              src={createReleaseImage}
              alt=""
              style={{maxHeight: '25rem'}}
            />
          </div>
        </div>

        {this.renderFooter()}
      </Form>
    );
  }
}
